/**
 * Canonical deep-link contract for the EU historical COMPARE, shared by every
 * click surface (DrugDetailPage's two citation handlers + SearchPage's new-tab
 * opener) so the URL that lands the compare is written in ONE place.
 *
 * Canonical query shape (drug_id lives in the PATH, not here):
 *
 *   /drug/:drugId?source=eu-history-compare
 *                 &from=<old vstr>&to=<new vstr>   ← the version pair
 *                 &section=<number>                ← the cited section
 *                 &part=<document part>             ← smpc|annex_ii|labelling|package_leaflet
 *                 &presentation=<normalized key>   ← the cited strength
 *                 &block=<content-addressed id>     ← the EXACT change block
 *
 * The pair + section + presentation + block are the FULL citation target. The
 * compare uses ``block`` (when present) to resolve the precise old/new spans via
 * /eu-history/.../compare-block and outline them; it falls back to ``section``
 * landing when the block is absent or 404s. Consumes ``EU_HISTORY_COMPARE_KEYS``
 * on close so a refresh mid-modal re-lands, but a back/close leaves a clean URL.
 */
import type { SimpleCitation } from '../types/qa';

export const EU_HISTORY_COMPARE_SOURCE = 'eu-history-compare';

// Every query key this feature owns. Cleared together when the modal closes and
// before applying a fresh target, so a stale presentation/block never leaks from
// one citation click into the next.
export const EU_HISTORY_COMPARE_KEYS = [
  'source', 'from', 'to', 'section', 'part', 'presentation', 'block',
] as const;

/**
 * Serialize an ``eu_historical`` citation into the canonical compare query
 * params. Returns ``null`` when the citation isn't an eu_historical one (so a
 * caller can fall through to other routing). ``drug_id`` is intentionally NOT
 * emitted — it belongs in the route path; DrugDetailPage is already on the drug
 * and SearchPage supplies it when building the ``/drug/:id`` URL.
 *
 * Only fields the citation actually carries are set — an inline section link
 * with no interval yields ``source`` + ``section`` alone, which the launcher
 * treats as "no forced pair" rather than silently picking one.
 */
export function euHistoricalCompareParams(
  cit: SimpleCitation,
): URLSearchParams | null {
  if (cit.document_type !== 'eu_historical') return null;
  const p = new URLSearchParams({ source: EU_HISTORY_COMPARE_SOURCE });
  if (cit.from_version) p.set('from', cit.from_version);
  if (cit.to_version) p.set('to', cit.to_version);
  if (cit.section_number) p.set('section', cit.section_number);
  // part namespaces the block (flag-ON Parser-B path); legacy citations omit it
  // and the compare treats absence as 'smpc'.
  if (cit.document_part) p.set('part', cit.document_part);
  if (cit.presentation_key) p.set('presentation', cit.presentation_key);
  if (cit.block_id) p.set('block', cit.block_id);
  return p;
}

/**
 * Merge the compare target into an existing search-param set for an in-page
 * navigation (DrugDetailPage): clears any prior compare keys first so a second
 * click can't inherit the first click's presentation/block, then applies the
 * fresh target. Returns a NEW URLSearchParams (caller passes it to
 * ``setSearchParams``). Returns ``null`` when the citation isn't eu_historical.
 */
export function applyEuHistoricalCompareTarget(
  current: URLSearchParams,
  cit: SimpleCitation,
): URLSearchParams | null {
  const target = euHistoricalCompareParams(cit);
  if (!target) return null;
  const next = new URLSearchParams(current);
  EU_HISTORY_COMPARE_KEYS.forEach((k) => next.delete(k));
  target.forEach((v, k) => next.set(k, v));
  return next;
}

/**
 * Disambiguate an inline "§X" section link that carries only (drug, section).
 * Returns the single citation to open, or ``null`` when the section changed
 * across MORE THAN ONE distinct target (ambiguous → the caller must do NOTHING
 * and require the reader to pick an interval-labelled source chip; it must NOT
 * fall back to a current-section scroll, which would land on the wrong era).
 * One match, or many that all name the same interval / same archived version,
 * resolve unambiguously to the first.
 */
export function pickInlineEuHistoricalCitation(
  matches: SimpleCitation[],
): SimpleCitation | null {
  if (matches.length === 0) return null;
  if (matches.length === 1) return matches[0]!;
  // Distinct citation TARGETS among the matches — a compare change is keyed by
  // its interval; an individual-version read by its version (+presentation).
  // MORE THAN ONE distinct target for a bare "§X" link is ambiguous (whether
  // that's two compare intervals OR two archived versions) → null, so the caller
  // requires an interval-labelled chip rather than guessing.
  const targets = new Set(
    matches.map((c) =>
      c.document_type === 'eu_historical'
        ? `hist:${c.from_version || ''}→${c.to_version || ''}`
        : `sec:${c.version || ''}:${c.presentation_key || ''}`),
  );
  return targets.size === 1 ? matches[0]! : null;
}

/** Remove the compare keys on modal close. CRITICAL: the keys are generic
 *  (``from``/``to``/``section``/…) and only belong to us when ``source`` is
 *  OURS. If it isn't, we touch NOTHING — deleting them would corrupt an
 *  unrelated navigation that happens to use the same param names. Returns a NEW
 *  set + whether anything changed (so the caller can skip a no-op history entry). */
export function clearEuHistoricalCompareTarget(
  current: URLSearchParams,
): { next: URLSearchParams; changed: boolean } {
  if (current.get('source') !== EU_HISTORY_COMPARE_SOURCE) {
    return { next: new URLSearchParams(current), changed: false };
  }
  const next = new URLSearchParams(current);
  let changed = false;
  for (const k of EU_HISTORY_COMPARE_KEYS) {
    if (next.has(k)) { next.delete(k); changed = true; }
  }
  return { next, changed };
}
